import { useEffect } from 'react';

/**
 * SEO component - sets document title and manages meta tags directly via DOM.
 * We manage tags manually instead of react-helmet-async because meta and link
 * tags weren't being applied consistently on lazy-loaded routes.
 * Each managed tag is marked with data-managed="seo" so we can find + update it.
 */

const SITE_URL = 'https://aplied.ai';
const DEFAULT_OG_IMAGE = 'https://aplied.ai/assets/og-image.jpg';

function upsertMeta({ selector, attrs }) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('data-managed', 'seo');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertLink({ rel, href }) {
  let el = document.head.querySelector(`link[rel="${rel}"][data-managed="seo"]`);
  if (!el) {
    el = document.head.querySelector(`link[rel="${rel}"]`) || document.createElement('link');
    el.setAttribute('data-managed', 'seo');
    el.setAttribute('rel', rel);
    if (!el.parentNode) document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

const SEO = ({
  title = 'Aplied.ai | AI Consultancy for Mining & Industrial Sectors',
  description = 'Expert AI consultancy and engineering services for mining and industrial companies. Transform operations with predictive maintenance, process optimization, and data-driven insights.',
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  schema,
}) => {
  const fullUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  useEffect(() => {
    // Title
    document.title = title;

    // Primary meta
    upsertMeta({ selector: 'meta[name="title"]', attrs: { name: 'title', content: title } });
    upsertMeta({ selector: 'meta[name="description"]', attrs: { name: 'description', content: description } });
    upsertMeta({ selector: 'meta[name="robots"]', attrs: { name: 'robots', content: 'index, follow' } });
    upsertMeta({ selector: 'meta[name="author"]', attrs: { name: 'author', content: 'Aplied.ai' } });

    // Canonical
    upsertLink({ rel: 'canonical', href: fullUrl });

    // Open Graph
    upsertMeta({ selector: 'meta[property="og:type"]', attrs: { property: 'og:type', content: ogType } });
    upsertMeta({ selector: 'meta[property="og:url"]', attrs: { property: 'og:url', content: fullUrl } });
    upsertMeta({ selector: 'meta[property="og:title"]', attrs: { property: 'og:title', content: title } });
    upsertMeta({ selector: 'meta[property="og:description"]', attrs: { property: 'og:description', content: description } });
    upsertMeta({ selector: 'meta[property="og:image"]', attrs: { property: 'og:image', content: ogImage } });
    upsertMeta({ selector: 'meta[property="og:site_name"]', attrs: { property: 'og:site_name', content: 'Aplied.ai' } });

    // Twitter
    upsertMeta({ selector: 'meta[name="twitter:card"]', attrs: { name: 'twitter:card', content: 'summary_large_image' } });
    upsertMeta({ selector: 'meta[name="twitter:url"]', attrs: { name: 'twitter:url', content: fullUrl } });
    upsertMeta({ selector: 'meta[name="twitter:title"]', attrs: { name: 'twitter:title', content: title } });
    upsertMeta({ selector: 'meta[name="twitter:description"]', attrs: { name: 'twitter:description', content: description } });
    upsertMeta({ selector: 'meta[name="twitter:image"]', attrs: { name: 'twitter:image', content: ogImage } });

    // Schema.org JSON-LD
    let schemaEl = document.head.querySelector('script[type="application/ld+json"][data-managed="seo"]');
    if (schema) {
      if (!schemaEl) {
        schemaEl = document.createElement('script');
        schemaEl.setAttribute('type', 'application/ld+json');
        schemaEl.setAttribute('data-managed', 'seo');
        document.head.appendChild(schemaEl);
      }
      schemaEl.textContent = JSON.stringify(schema);
    } else if (schemaEl) {
      schemaEl.remove();
    }
  }, [title, description, fullUrl, ogImage, ogType, schema]);

  return null;
};

export default SEO;
